// ======================================================================
// 📁 File: BusinessPartnerListPage.jsx
// ======================================================================

import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
} from "react";

import { AuthContext } from "../../../../../context/AuthContext";

import businessPartnerService from "./services/businessPartnerService";

import BusinessPartnerFormDrawer from "./components/BusinessPartnerFormDrawer";
import BPStatusBadge from "./components/BPStatusBadge";

// ----------------------------------------------------------------------
export default function BusinessPartnerListPage() {
  const { permissions } = useContext(AuthContext);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const [search, setSearch] = useState("");
  const [role, setRole] = useState("ALL");
  const [status, setStatus] = useState("ALL");

  // Drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState("create");
  const [selectedRow, setSelectedRow] = useState(null);

  // ------------------------------------------------------------
  // LOAD BUSINESS PARTNERS
  // ------------------------------------------------------------
  const loadPartners = useCallback(async () => {
    setLoading(true);
    try {
      const res =
        await businessPartnerService.listBusinessPartners({
          search,
          role,
          status,
        });

      setRows(res.items || []);
    } catch (err) {
      console.error("Failed to load business partners", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search, role, status]);

  useEffect(() => {
    loadPartners();
  }, [loadPartners]);

  // ------------------------------------------------------------
  // ACTIVATE
  // ------------------------------------------------------------
  const activatePartner = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to activate this business partner?"
      )
    )
      return;

    try {
      await businessPartnerService.activateBusinessPartner(id);
      loadPartners();
    } catch (err) {
      console.error(err);
      alert("Activation failed");
    }
  };

  // ------------------------------------------------------------
  // DEACTIVATE
  // ------------------------------------------------------------
  const deactivatePartner = async (id) => {
    if (
      !window.confirm(
        "Are you sure you want to deactivate this business partner?"
      )
    )
      return;

    try {
      await businessPartnerService.deactivateBusinessPartner(id);
      loadPartners();
    } catch (err) {
      console.error(err);
      alert("Deactivation failed");
    }
  };

  // ------------------------------------------------------------
  // DRAWER
  // ------------------------------------------------------------
  const openCreateDrawer = () => {
    setSelectedRow(null);
    setDrawerMode("create");
    setDrawerOpen(true);
  };

  const openEditDrawer = (row) => {
    setSelectedRow(row);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedRow(null);
  };

  // ------------------------------------------------------------
  // FILTER (CLIENT SIDE)
  // ------------------------------------------------------------
  const filteredRows = rows.filter((r) => {
    const q = search.toLowerCase();

    const matchesSearch =
      !q ||
      String(r.bp_code || "")
        .toLowerCase()
        .includes(q) ||
      String(r.bp_name || "")
        .toLowerCase()
        .includes(q);

    const matchesRole =
      role === "ALL" || r.roles?.includes(role);

    const matchesStatus =
      status === "ALL" ||
      (status === "ACTIVE" && r.is_active) ||
      (status === "INACTIVE" && !r.is_active);

    return (
      matchesSearch &&
      matchesRole &&
      matchesStatus
    );
  });

  // ------------------------------------------------------------
  // RENDER
  // ------------------------------------------------------------
  return (
    <div>
      {/* Header */}
      <div
        style={{
          background:
            "linear-gradient(90deg,#0f766e,#14b8a6,#5eead4)",
          borderRadius: 12,
          padding: "16px 20px",
          color: "#fff",
          marginBottom: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div>
          <h4 style={{ margin: 0 }}>
            Business Partner
          </h4>
          <div style={{ fontSize: 13 }}>
            Vendors, customers, transporters &
            job workers
          </div>
        </div>

        {permissions?.BP_CREATE?.create && (
          <button
            className="btn btn-light"
            onClick={openCreateDrawer}
          >
            + Create Partner
          </button>
        )}
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 16,
        }}
      >
        <input
          className="form-control"
          placeholder="Search"
          style={{ maxWidth: 260 }}
          value={search}
          onChange={(e) =>
            setSearch(e.target.value)
          }
        />

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={role}
          onChange={(e) =>
            setRole(e.target.value)
          }
        >
          <option value="ALL">All Roles</option>
          <option value="VENDOR">Vendor</option>
          <option value="CUSTOMER">Customer</option>
          <option value="TRANSPORTER">
            Transporter
          </option>
          <option value="JOB_WORKER">
            Job Worker
          </option>
        </select>

        <select
          className="form-select"
          style={{ maxWidth: 160 }}
          value={status}
          onChange={(e) =>
            setStatus(e.target.value)
          }
        >
          <option value="ALL">All</option>
          <option value="ACTIVE">
            Active
          </option>
          <option value="INACTIVE">
            Inactive
          </option>
        </select>

        <button
          className="btn btn-primary"
          onClick={loadPartners}
        >
          Apply
        </button>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-body p-0">
          <table className="table table-bordered">
            <thead className="table-light">
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Roles</th>
                <th>Status</th>
                <th width="180">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredRows.map((r) => (
                <tr key={r.id}>
                  <td>{r.bp_code}</td>
                  <td>{r.bp_name}</td>
                  <td>{r.bp_type}</td>
                  <td>
                    {r.roles?.join(", ")}
                  </td>
                  <td>
                    <BPStatusBadge
                      status={
                        r.is_active
                          ? "ACTIVE"
                          : "INACTIVE"
                      }
                    />
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-outline-primary me-1"
                      onClick={() =>
                        openEditDrawer(r)
                      }
                    >
                      Edit
                    </button>

                    {r.is_active ? (
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() =>
                          deactivatePartner(
                            r.id
                          )
                        }
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={() =>
                          activatePartner(r.id)
                        }
                      >
                        Activate
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Drawer */}
      <BusinessPartnerFormDrawer
        open={drawerOpen}
        mode={drawerMode}
        record={selectedRow}
        onClose={closeDrawer}
        onSaved={loadPartners}
      />
    </div>
  );
}
